<template>
<div class="px-1 pt-1">
  <div class="input-group pb-3">
    <button class="btn btn-outline-primary" @click="routeToCreateView()">
      <font-awesome-icon :icon="['fas', 'rectangle-list']" />
      {{ $t('action.newEllipsis') }}
    </button>
    <button v-if="userSettingsStore.data.developerMode" class="btn btn-outline-primary d-none d-lg-inline-block" @click="downloadAsExcel()">
      <font-awesome-icon :icon="['fas', 'file-excel']" />
    </button>
    <span class="input-group-text ms-2">
      <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
    </span>
    <input type="search" class="form-control" v-model="itemFilter" :placeholder="$t('action.search')" :disabled="isLoading">
    <button class="btn btn-outline-secondary" @click="resetFilter()" :disabled="isLoading" v-if="itemFilter || selectedRestockPullType !== null">
      <font-awesome-icon :icon="['fas', 'filter-slash']" />
    </button>
    <button v-for="pullType in RESTOCK_PULL_TYPE" :key="pullType"
      type="button"
      class="btn btn-outline-secondary"
      :class="{ active: selectedRestockPullType === pullType }"
      @click="toggleRestockPullTypeFilter(pullType)"
      :disabled="isLoading"
      :title="$t(`enum.restockPullType.${pullType}`)">
      <RestockPullTypeIcon :restock-pull-type="pullType" />
    </button>
  </div>

  <div v-if="error" class="alert alert-danger">
    {{ error }}
  </div>
  <div v-else-if="uiList" id="content-page-table" ref="scrollContainerRef" class="table-fix-head" :style="fixHeadCalculatedStyle" @scroll.passive="onScroll">
    <table v-if="uiList.length > 0" class="table table-hover table-sm table-fit align-middle" :style="colWidths.length > 0 ? 'table-layout: fixed !important' : ''">
      <colgroup v-if="colWidths.length > 0">
        <col v-for="(w, i) in colWidths" :key="i" :style="{ width: w + 'px' }" />
      </colgroup>
      <thead>
        <tr>
          <th class="d-none d-md-table-cell"></th>
          <th>
            {{ $t('field.generic.item') }}
            <font-awesome-icon :icon="['fas', 'sort-up']" />
          </th>
          <th class="d-none d-xl-table-cell">{{ $t('page.item.table.baseUnitCode') }} / {{ $t('page.item.table.logisticUnitName') }}</th>
          <th class="d-none d-lg-table-cell">{{ $t('page.item.table.tracking') }}</th>
          <th class="d-none d-lg-table-cell">{{ $t('page.item.table.isBillableShort') }}</th>
          <th class="d-none d-lg-table-cell" :title="$t('page.item.form.restockPullType')">{{ $t('field.item.restockPullType') }}</th>
          <th></th>
          <th v-if="userSettingsStore.data.showBarcode"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="topPadHeight > 0" aria-hidden="true">
          <td :colspan="100" :style="{ padding: 0, border: 0, height: topPadHeight + 'px' }"></td>
        </tr>
        <tr v-for="item in visibleRows" :key="item.id" ref="rowRefs">
          <td class="d-none d-md-table-cell">
            <div class="btn-group">
              <RouterLink :to="{ name: 'item', params: { itemId: item.id }}" class="btn btn-outline-secondary btn-sm">
                <font-awesome-icon :icon="['fas', 'pen']" />
              </RouterLink>
              <RouterLink :to="{ name: 'stocks', params: { itemId: item.id }}" :title="$t('menu.stocks')" class="btn btn-outline-secondary btn-sm d-none d-md-inline-block">
                <font-awesome-icon :icon="['fas', 'boxes-stacked']" />
              </RouterLink>
              <RouterLink :to="{ name: 'stock-change', params: { itemId: item.id }}" :title="$t('menu.stock-changes')" class="btn btn-outline-secondary btn-sm d-none d-md-inline-block">
                <font-awesome-icon :icon="['fas', 'plus-minus']" />
              </RouterLink>
            </div>
          </td>
          <td @click="toggleExpand(item.id)" :style="replacementItemsByItemId(item.id).length > 0 ? 'cursor: pointer' : ''">
            <span v-if="replacementItemsByItemId(item.id).length > 0" :style="{ display: 'inline-block', transition: 'transform 0.15s', transform: expandedBaseIds.has(item.id) ? 'rotate(90deg)' : 'none' }"><svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'><path fill='none' stroke='rgba(0,0,0,.5)' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M5 14l6-6-6-6' /></svg></span>
            <span v-if="!item.isInternalGenerated">
              <img alt="item manager icon" src="/icon-simple.svg" class="icon-x1 me-1" />
            </span>
            <span class="fw-bold" :class="{ 'text-primary': !isReplacementItem(item.id), 'text-melodi': isReplacementItem(item.id) }" v-html="highlightMatch(item.name, itemFilter) + '&#8203;'"></span><sup class="text-secondary ms-1" v-html="highlightMatch(item.code, itemFilter)"></sup>
            <br>
            <span v-if="item.name.localeCompare(item.description) != 0" class="fs-6 text-melodi">{{ item.description }}</span>
          </td>
          <td class="d-none d-xl-table-cell">
            <template v-for="tradeItem in tradeItemsByItemId(item.id)" :key="tradeItem.id">
              <div>
                <span>{{ tradeItem.code }}</span>
                <span v-if="tradeItem.factor > 1">
                  <span class="text-melodi" :class="{ 'fw-bold': tradeItem.unitCode === item.baseUnitCode }">&nbsp;{{ tradeItem.unitCode }}&nbsp;à&nbsp;{{ tradeItem.factor }}&nbsp;{{ getUnitCode(item.id, item.baseUnitCode) }}</span>
                </span>
                <span v-else>
                  <span class="text-melodi" :class="{ 'fw-bold': tradeItem.unitCode === item.baseUnitCode }">&nbsp;{{ tradeItem.unitCode }}</span>
                </span>
              </div>
            </template>
          </td>
          <template v-if="!isReplacementItem(item.id)">
            <td class="d-none d-lg-table-cell">
              <font-awesome-layers v-if="item.trackLotNumber && !item.allowCalculatedLot" class="me-1 text-primary">
                <font-awesome-icon :icon="['far', 'rectangle-wide']" />
                <font-awesome-layers-text value="LOT" transform="shrink-8" />
              </font-awesome-layers>
              <font-awesome-layers v-else-if="item.trackLotNumber && item.allowCalculatedLot" class="me-1 text-melodi">
                <font-awesome-icon :icon="['far', 'rectangle-wide']" />
                <font-awesome-layers-text value="AUTO LOT" transform="shrink-8 up-6" />
              </font-awesome-layers>
              <font-awesome-icon v-else :icon="['fas', 'dot']" class="me-1 invisible" />
              <font-awesome-icon v-if="item.trackBestBeforeDate || item.trackExpirationDate" :icon="['fas', 'hourglass-end']" class="me-1 text-secondary" />
              <span v-if="item.trackBestBeforeDate && item.bestBeforeLeadTime > -1" class="text-nowrap">{{ $t('global.days', item.bestBeforeLeadTime) }}</span>
              <span v-else-if="item.trackExpirationDate && item.expirationLeadTime > -1" class="text-nowrap">{{ $t('global.days', item.expirationLeadTime) }}</span>
            </td>
            <td class="d-none d-lg-table-cell">
              <font-awesome-layers>
                <template v-if="item.isBillable">
                  <font-awesome-icon :icon="['fas', 'coins']" class="text-melodi" />
                </template>
                <template v-else>
                  <font-awesome-icon :icon="['fas', 'slash']" :mask="['fas', 'coins']" class="text-muted" />
                  <font-awesome-icon :icon="['fas', 'slash']" transform="up-1 right-1" class="text-danger" />
                </template>
              </font-awesome-layers>
            </td>
            <td class="d-none d-lg-table-cell">
              <RestockPullTypeIcon :restock-pull-type="item.siteRestockPullType" class="text-secondary" />
              <span v-if="item.canOrderManually && item.siteRestockPullType !== RESTOCK_PULL_TYPE.onDemand" class="ms-1 text-secondary" :title="$t('page.item.table.canOrderManually')">
                <font-awesome-icon :icon="['fas', 'cart-shopping']" fixed-width />
              </span>
              <a v-if="openOrdersQuantity(item.id).length > 0 || draftQuantity(item.id).length > 0" href="#" class="text-decoration-none" :title="$t('page.item.openOrders.title')" @click.prevent.stop="openOrdersModal?.open(item)">
                <span v-for="entry in openOrdersQuantity(item.id)" :key="entry.unitCode" class="badge bg-info text-dark ms-1">
                  <font-awesome-icon :icon="['fas', 'truck']" class="me-1" />{{ entry.quantity }} {{ entry.unitCode }}
                </span>
                <span v-for="entry in draftQuantity(item.id)" :key="entry.unitCode" class="badge bg-warning text-dark ms-1">
                  <font-awesome-icon :icon="['fas', 'cart-shopping']" class="me-1" />{{ entry.quantity }} {{ entry.unitCode }}
                </span>
              </a>
            </td>
            <td>
              <div class="btn-group">
                <AddToOrderDraft :item-id="item.id" :restock-pull-type="item.siteRestockPullType" :can-order-manually="item.canOrderManually" />
              </div>
              <div class="d-none d-lg-inline-block">
                <span v-if="supplierNameForItem(item.id)" class="ms-2 text-secondary small text-nowrap">{{ supplierNameForItem(item.id) }}</span>
              </div>
            </td>
          </template>
          <template v-else>
            <td class="d-none d-lg-table-cell"></td>
            <td class="d-none d-xl-table-cell"></td>
            <td class="d-none d-lg-table-cell"></td>
            <td></td>
          </template>
          <td v-if="userSettingsStore.data.showBarcode" class="bg-white p-1">
            <TradeItemBarcode :itemId="item.id" :itemSignature="emptySignature" :size="'3rem'" />
          </td>
        </tr>
        <tr v-if="bottomPadHeight > 0" aria-hidden="true">
          <td :colspan="100" :style="{ padding: 0, border: 0, height: bottomPadHeight + 'px' }"></td>
        </tr>
      </tbody>
    </table>
    <span v-else>{{ $t('error.noData') }}</span>
  </div>
  <div v-else class="loading">
    {{ $t('status.loadingEllipsis') }}
  </div>
  <OpenOrdersForItemModal ref="openOrdersModal" />
</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useSessionStore, useItemsStore, useTradeItemsStore, useReplacementItemsStore, useUserSettingsStore, useItemSiteSettingsStore, useOrdersStore, useSupplierItemsStore } from '@/stores';
import TradeItemBarcode from '@/components/diagram/TradeItemBarcode.vue';
import RestockPullTypeIcon from "@/components/masterdata/RestockPullTypeIcon.vue";
import AddToOrderDraft from "@/components/orders/AddToOrderDraft.vue";
import OpenOrdersForItemModal from "@/components/orders/OpenOrdersForItemModal.vue";
import ItemSignatureRaw from "@/models/frontend/itemSignatureRaw";
import Tenant from "@/models/frontend/Tenant";
import Item from "@/models/frontend/Item";
import { RESTOCK_PULL_TYPE, RestockPullType } from "@/models/Enums";
import { router } from "@/services";
import api from "@/services/api";

const settingsStore = useSessionStore();
const userSettingsStore = useUserSettingsStore();
const itemsStore = useItemsStore();
const tradeItemsStore = useTradeItemsStore();
const replacementItemsStore = useReplacementItemsStore();
const itemSiteSettingStore = useItemSiteSettingsStore();
const ordersStore = useOrdersStore();
const supplierItemsStore = useSupplierItemsStore();

const supplierNameForItem = (itemId: number) => {
  const baseItemId = replacementItemsStore.getBaseItem(itemId);
  const rootItemId = baseItemId ?? itemId;
  return supplierItemsStore.getByItemId(rootItemId)?.supplier?.name;
};

const isLoading = ref(false);
const itemFilter = ref("");
const selectedRestockPullType = ref<RestockPullType | null>(null);
const emptySignature = new ItemSignatureRaw(undefined, undefined, undefined);
const error = ref("");
const offsetTop = ref(0);
const openOrdersModal = ref<InstanceType<typeof OpenOrdersForItemModal> | null>(null);

// Replaces the Bootstrap collapse so virtual scrolling can keep an accurate
// row count — collapsed-via-CSS replacement rows would otherwise occupy index
// slots without contributing height, breaking spacer math.
const expandedBaseIds = ref(new Set<number>());

const scrollContainerRef = ref<HTMLElement | null>(null);
const rowRefs = ref<HTMLElement[]>([]);
const rowHeight = ref(48);
const scrollTop = ref(0);
const viewportHeight = ref(0);
const overscan = 10;
const colWidths = ref<number[]>([]);

class RowEntry implements Item {
  constructor(
    public id: number,
    public tenant: Tenant,
    public code: string,
    public name: string,
    public description: string,
    public baseUnitCode: string,
    public requiresLabel: boolean,
    public restockPullType: RestockPullType,
    public trackBestBeforeDate: boolean,
    public trackExpirationDate: boolean,
    public trackLotNumber: boolean,
    public bestBeforeLeadTime: number,
    public expirationLeadTime: number,
    public siteRestockPullType: RestockPullType,
    public isInternalGenerated: boolean,
    public isBillable: boolean,
    public allowCalculatedLot: boolean,
    public canOrderManually: boolean
  ) { }
}

function syncViewportMetrics() {
  const el = scrollContainerRef.value;
  if (!el) return;
  scrollTop.value = el.scrollTop;
  viewportHeight.value = el.clientHeight;
}

function onScroll() {
  syncViewportMetrics();
}

async function measureRowHeight() {
  await nextTick();
  const sample = rowRefs.value?.[0];
  if (sample) {
    const h = sample.getBoundingClientRect().height;
    if (h > 0 && Math.abs(h - rowHeight.value) > 0.5) {
      rowHeight.value = h;
    }
  }
}

// Pin column widths so virtual-scrolling new rows in/out doesn't cause the
// table's auto-layout to recalculate and the columns to jump around. Use the
// max width per column across all currently rendered rows so that replacement
// rows (with empty cells) don't yield artificially narrow widths.
async function measureColumnWidths() {
  // .table-fit only switches the table to width:auto at md+ (theme.scss).
  // Below md the table stays at width:100% and applying the colgroup +
  // table-layout:fixed disrupts the auto layout — the name column collapses
  // to roughly its widest single word and the AddToOrderDraft cell takes up
  // the leftover slot. Skip the pinning there; the few visible columns on a
  // phone don't really jitter as virtual rows scroll in.
  if (!window.matchMedia('(min-width: 768px)').matches) {
    if (colWidths.value.length > 0) {
      colWidths.value = [];
      await nextTick();
    }
    await measureRowHeight();
    return;
  }
  colWidths.value = [];
  await nextTick();
  const rows = rowRefs.value;
  if (!rows || rows.length === 0) return;
  let maxes: number[] | null = null;
  for (const row of rows) {
    if (!row) continue;
    const tds = row.querySelectorAll<HTMLTableCellElement>('td');
    if (tds.length === 0) continue;
    if (maxes === null) maxes = new Array(tds.length).fill(0);
    Array.from(tds).forEach((td, i) => {
      if (i < maxes!.length) {
        const cur = maxes![i] ?? 0;
        maxes![i] = Math.max(cur, Math.ceil(td.getBoundingClientRect().width));
      }
    });
  }
  if (maxes) colWidths.value = maxes;
  await measureRowHeight();
}

const onWindowResize = () => {
  syncViewportMetrics();
  measureColumnWidths();
};

function toggleExpand(itemId: number) {
  if (replacementItemsByItemId(itemId).length === 0) return;
  const next = new Set(expandedBaseIds.value);
  if (next.has(itemId)) {
    next.delete(itemId);
  } else {
    next.add(itemId);
  }
  expandedBaseIds.value = next;
}

onMounted(async () => {
  // getBoundingClientRect().top is viewport-relative, but the height formula
  // `100vh - offsetTop` only makes the document fit the viewport when offsetTop
  // is document-relative. If the body is scrolled when this view mounts (e.g.
  // re-entering with the previous scroll position preserved), the viewport-top
  // is smaller than the document-top, the container ends up too tall, and the
  // body overflows — producing the unwanted outer scrollbar. Add the current
  // page scroll to convert to a document-relative offset.
  const containerRect = scrollContainerRef.value?.getBoundingClientRect()
    ?? document.querySelector('#content-page-table')?.getBoundingClientRect();
  offsetTop.value = containerRect ? containerRect.top + window.scrollY : 0;
  await Promise.all([
    itemsStore.fetchOpenOrdersQuantity(),
    ordersStore.fetchDraftLines(),
  ]);
  await nextTick();
  syncViewportMetrics();
  await measureColumnWidths();
  window.addEventListener('resize', onWindowResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize);
});

watch(() => settingsStore.selectedSiteId, async () => {
  await Promise.all([
    itemsStore.fetchOpenOrdersQuantity(),
    ordersStore.fetchDraftLines(),
  ]);
});

const baseList = computed(() => {
  return itemsStore.data.map((item) => new RowEntry(
    item.id,
    item.tenant,
    item.code,
    item.name,
    item.description,
    item.baseUnitCode,
    item.requiresLabel,
    item.restockPullType,
    item.trackBestBeforeDate,
    item.trackExpirationDate,
    item.trackLotNumber,
    item.bestBeforeLeadTime,
    item.expirationLeadTime,
    itemSiteSettingStore.getByItemId(item.id)?.restockPullType ?? item.restockPullType,
    item.isInternalGenerated,
    item.isBillable,
    item.allowCalculatedLot,
    itemSiteSettingStore.getByItemId(item.id)?.canOrderManually ?? false
  ));
});

const resetFilter = () => {
  itemFilter.value = "";
  selectedRestockPullType.value = null;
}

const toggleRestockPullTypeFilter = (pullType: RestockPullType) => {
  selectedRestockPullType.value = selectedRestockPullType.value === pullType ? null : pullType;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function highlightMatch(text: string, search: string): string {
  const escaped = escapeHtml(text);
  if (!search) return escaped;
  const idx = text.toLowerCase().indexOf(search.toLowerCase());
  if (idx === -1) return escaped;
  return escapeHtml(text.substring(0, idx))
    + '<mark class="p-0">' + escapeHtml(text.substring(idx, idx + search.length)) + '</mark>'
    + escapeHtml(text.substring(idx + search.length));
}

const getItemSortName = (item: Item) => {
  const baseItem = replacementItemsStore.getBaseItem(item.id);
  if (baseItem !== undefined) {
    return itemsStore.getById(baseItem).name + "_" + item.name;
  } else {
    return item.name;
  }
}

const tradeItemsByItemId = (itemId: number) => tradeItemsStore.getByItemId(itemId);

const getUnitCode = (itemId: number, baseUnitCode: string) => {
  return tradeItemsStore.getBaseByItemId(itemId)?.unitCode ?? baseUnitCode;
};

const replacementItemsByItemId = (itemId: number) => {
  const replacements = replacementItemsStore.getReplacementsForItem(itemId);
  return replacements.map((item) => itemsStore.getById(item.replacementItemId)).sort((a, b) => a.name.localeCompare(b.name))
};

const isReplacementItem = (itemId: number) => {
  return replacementItemsStore.getBaseItem(itemId) !== undefined;
};

const openOrdersQuantity = (itemId: number) => itemsStore.openOrdersQuantityByItemId[itemId] ?? [];

const draftQuantity = (itemId: number) => ordersStore.draftQuantityByItemId[itemId] ?? [];

const uiList = computed(() => {
  const filtered = baseList.value.filter((item: RowEntry) => {
    return (
      (item.name.toLowerCase().indexOf(itemFilter.value.toLowerCase()) != -1 || item.code.toLowerCase().indexOf(itemFilter.value.toLowerCase()) != -1) &&
      (selectedRestockPullType.value === null || selectedRestockPullType.value === item.siteRestockPullType)
    );
  }).sort((a: RowEntry, b: RowEntry) => getItemSortName(a).localeCompare(getItemSortName(b)));

  // Drop replacement items whose base isn't in the expanded set so the
  // virtual scroller's index/spacer math reflects only rendered rows.
  return filtered.filter((item: RowEntry) => {
    const baseId = replacementItemsStore.getBaseItem(item.id);
    return baseId === undefined || expandedBaseIds.value.has(baseId);
  });
});

const visibleRange = computed(() => {
  const rh = rowHeight.value || 1;
  const total = uiList.value.length;
  const start = Math.max(0, Math.floor(scrollTop.value / rh) - overscan);
  const visibleCount = Math.ceil(viewportHeight.value / rh) + 2 * overscan;
  const end = Math.min(total, start + visibleCount);
  return { start, end };
});

const visibleRows = computed(() => uiList.value.slice(visibleRange.value.start, visibleRange.value.end));
const topPadHeight = computed(() => visibleRange.value.start * rowHeight.value);
const bottomPadHeight = computed(() => Math.max(0, uiList.value.length - visibleRange.value.end) * rowHeight.value);

watch(() => uiList.value.length, async () => {
  await measureRowHeight();
});

watch(() => userSettingsStore.data.showBarcode, async () => {
  await measureColumnWidths();
});

watch(itemFilter, () => {
  if (scrollContainerRef.value) {
    scrollContainerRef.value.scrollTop = 0;
    scrollTop.value = 0;
  }
});

// Re-pin column widths after expansion changes since adding/removing
// replacement rows can introduce/remove cells with different content widths.
watch(expandedBaseIds, async () => {
  await measureColumnWidths();
}, { deep: true });

const routeToCreateView = async () => {
  router.push({ name: 'item-create' });
}

const downloadAsExcel = () => {
  api.get<Blob>(`v1.0/items/.excel?siteId=${settingsStore.selectedSiteId}`, {
    responseType: 'blob'
  })
    .then(response => {
      const blob = new Blob([response.data], { type: String(response.headers['content-type'] ?? '') });
      const link = document.createElement('a');
      const contentDisposition = response.headers['content-disposition'] as string;
      const fileName = parseContentDisposition(contentDisposition);
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', fileName ?? 'items.xlsx');
      link.click();
      URL.revokeObjectURL(link.href);
    })
    .catch(console.error);
};

function parseContentDisposition(contentDisposition: string): string | null {
  // Regular expression to match filename*
  const filenameStarRegex = /filename\*=([^;]+)/;
  // Regular expression to match filename
  const filenameRegex = /filename=([^;]+)/;

  let matches = filenameStarRegex.exec(contentDisposition);
  if (matches && matches[1]) {
    // Decode the encoded filename*
    return decodeURIComponent(matches[1].replace(/^UTF-8''/, ''));
  }

  matches = filenameRegex.exec(contentDisposition);
  if (matches && matches[1]) {
    // Return the plain filename, stripping any surrounding quotes
    return matches[1].replace(/(^"|"$)/g, '');
  }

  // Return null if no filename is found
  return null;
}

const fixHeadCalculatedStyle = computed(() => {
  return {
    height: `calc(100vh - ${offsetTop.value}px)`,
  };
});
</script>

<style scoped>
.icon-x1 {
  width: .8em;
  height: .8em;
  vertical-align: baseline;
}
</style>
